import React, { useState } from 'react';
import { DeployedAPI } from '../../services/apiService';
import { apiService } from '../../services/apiService';
import './TestCommandModal.css';

interface TestCommandModalProps {
  apiConfig: DeployedAPI;
  onClose: () => void;
}

const TestCommandModal: React.FC<TestCommandModalProps> = ({ apiConfig, onClose }) => {
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [powershell, setPowershell] = useState<string>('');
  const [curl, setCurl] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Load test commands asynchronously to get the correct backend URL from environment config
  React.useEffect(() => {
    const loadCommands = async () => {
      setLoading(true);
      try {
        const commands = await apiService.getAPITestCommand(apiConfig);
        setPowershell(commands.powershell);
        setCurl(commands.curl);
      } catch (error) {
        console.error('Error loading test commands:', error);
        // Fallback to default
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const endpointPath = apiConfig.endpoint_path || apiConfig.api_url_path || '';
        const apiUrl = apiConfig.api_url || `${backendUrl}${endpointPath}`;
        setPowershell(`Invoke-RestMethod -Uri "${apiUrl}" -Method POST -Headers @{"Authorization"="Bearer ${apiConfig.api_key}"} -InFile "./test.jpg"`);
        setCurl(`curl -X POST "${apiUrl}" -H "Authorization: Bearer ${apiConfig.api_key}" --data-binary @test.jpg`);
      } finally {
        setLoading(false);
      }
    };
    loadCommands();
  }, [apiConfig]);

  const handleCopy = (command: string, type: string) => {
    navigator.clipboard.writeText(command);
    setCopiedCommand(type);
    setTimeout(() => setCopiedCommand(null), 2000);
  };

  return (
    <div className="test-command-overlay" onClick={onClose}>
      <div className="test-command-modal" onClick={(e) => e.stopPropagation()}>
        <div className="test-command-header">
          <h2>📋 Test Commands</h2>
          <button className="test-command-close" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="test-command-content">
          <p className="test-command-info">
            Copy and run these commands in your terminal. Make sure you have a test file (e.g., <code>test.jpg</code>) in the same directory.
          </p>

          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>Loading commands...</div>
          ) : (
            <>
              <div className="test-command-section">
                <h3>PowerShell (Windows)</h3>
                <div className="command-block">
                  <code className="command-text">{powershell}</code>
                  <button
                    className={`copy-command-btn ${copiedCommand === 'powershell' ? 'copied' : ''}`}
                    onClick={() => handleCopy(powershell, 'powershell')}
                  >
                    {copiedCommand === 'powershell' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>

              <div className="test-command-section">
                <h3>Bash/curl (Linux/Mac)</h3>
                <div className="command-block">
                  <code className="command-text">{curl}</code>
                  <button
                    className={`copy-command-btn ${copiedCommand === 'curl' ? 'copied' : ''}`}
                    onClick={() => handleCopy(curl, 'curl')}
                  >
                    {copiedCommand === 'curl' ? '✓ Copied!' : '📋 Copy'}
                  </button>
                </div>
              </div>

              <div className="test-command-note">
                <strong>Note:</strong> Replace <code>test.jpg</code> with your actual test file name. The API expects a file upload in the request body.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestCommandModal;

